'use client';

import { importToolsDrawerButtonSx } from '@/components/media/importToolsDrawerStyles';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface StickyActionBarProps {
  onSave: () => Promise<void> | void;
  onSaveAndSchedule: () => void;
  saving?: boolean;
  autosaveStatus?: 'idle' | 'saving' | 'saved' | 'error';
  /** Create page: immediate Instagram/Threads publish (separate from Save & Schedule). */
  onPostNow?: () => void;
  postNowDisabled?: boolean;
}

export default function StickyActionBar({
  onSave,
  onSaveAndSchedule,
  saving = false,
  autosaveStatus = 'idle',
  onPostNow,
  postNowDisabled = false,
}: StickyActionBarProps) {
  const statusText = autosaveStatus === 'saving' ? 'Saving…' : autosaveStatus === 'saved' ? 'Saved' : autosaveStatus === 'error' ? 'Save error' : '';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });

  return (
    <Box
      role="region"
      aria-label="Content actions"
      aria-busy={saving}
      data-floai-sticky-action-bar="1"
      sx={{
        position: 'fixed',
        bottom: { xs: 88, md: 16 },
        right: { xs: 0, md: 32 },
        left: { xs: 0, md: 'auto' },
        zIndex: 1400,
        display: 'flex',
        justifyContent: { xs: 'center', md: 'flex-end' },
        width: { xs: '100%', md: 'auto' },
        boxSizing: 'border-box',
        // No padding on this shell — avoids sx vs inline style merge issues on one node.
        pointerEvents: 'none',
        '& > *': { pointerEvents: 'auto' },
      }}
    >
      <Box
        data-floai-sticky-action-bar-inner="1"
        sx={{
          boxSizing: 'border-box',
          pt: '8px',
          pr: '8px',
          pb: '8px',
          pl: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          justifyContent: { xs: 'center', md: 'flex-start' },
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          width: { xs: '100%', md: 'auto' },
          backgroundColor: { xs: theme.palette.background.paper, md: 'transparent' },
          boxShadow: { xs: theme.shadows[3], md: 'none' },
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, flexGrow: { xs: 1, md: 0 }, justifyContent: { xs: 'center', md: 'flex-start' }, minWidth: 0 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => onSave()}
            disabled={saving}
            fullWidth={isMobile}
            aria-label={saving ? 'Saving content' : 'Save content'}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button
            variant="outlined"
            onClick={onSaveAndSchedule}
            disabled={saving}
            fullWidth={isMobile}
            aria-label="Save and schedule content"
            sx={importToolsDrawerButtonSx}
          >
            Save & Schedule
          </Button>
          {onPostNow ? (
            <Button
              variant="outlined"
              color="secondary"
              onClick={onPostNow}
              disabled={saving || postNowDisabled}
              fullWidth={isMobile}
              aria-label="Post now to connected networks"
              sx={{
                ...importToolsDrawerButtonSx,
                '&:hover': {
                  borderColor: 'secondary.main',
                  bgcolor: 'action.hover',
                },
              }}
            >
              Post now
            </Button>
          ) : null}
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', ml: { xs: 0, md: 1 } }} aria-live="polite">
          {statusText}
        </Typography>
      </Box>
    </Box>
  );
}
